import { getAttachmentsToUpload } from '@/components/ui/FileUpload/utils';
import { UserRole } from '@/types/auth';
import { Clinic } from '@/types/clinic';
import { Inventory, InventoryFormValues } from '@/types/inventory';
import {
  ApiTableParams,
  ApiUsersParams,
  ApiPatientsParams,
  ApiOrdersParams,
  ApiOrderReportsParams,
  ApiOrderReportsNotesParams,
} from '@/types';
import { RequiredDocument, UploadedDocument } from '@/types/documents';
import {
  AttachmentWithOriginFileObj,
  CptToIcd10Mapping,
  HealthHistory,
  HealthHistoryGrouped,
  Imaging,
  Order,
  OrderConsult,
  OrderFormValues,
  OrderImagingFormInterface,
  OrderImagingReportCore,
  OrderReferral,
  OrderReferralStatus,
  OrderReport,
  OrderStatus,
} from '@/types/order';
import {
  Address,
  ClinicUser,
  ClinicUserRole,
  Patient,
  User,
  UserExtended,
  UserReferral,
} from '@/types/user';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { OrderBilling, OrderBillingFormValues } from '@/types/billing';
import { OrderPayment, Payment, PaymentFormValues } from '@/types/payment';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface PaginationResponse {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PaginationLinks {
  first: string;
  last: string;
  prev: string | null;
  next: string | null;
}

export interface ApiPaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
    has_more_pages: boolean;
    path: string;
    links: PaginationLinks;
  };
}

export interface AuthResponse {
  user?: User;
  token?: string;
  mfaRequired?: boolean;
  role?: UserRole;
  clinic?: Clinic;
  user_roles?: ClinicUserRole[];
  pin_activated?: boolean;
  required_documents?: RequiredDocument[];
}

export type ApiResponse<T = unknown> = {
  success?: boolean;
  message?: string;
  data?: T;
} & T;

interface MeResponse {
  token: string;
  user: User;
  role: UserRole;
  user_roles: ClinicUserRole[];
  clinic: Clinic;
  pin_activated: boolean;
  required_documents: RequiredDocument[];
}

export class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for auth token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If it's a 401 error from the refresh token endpoint, clear tokens and redirect
        if (
          error.response?.status === 401 &&
          originalRequest.url === '/auth/refresh'
        ) {
          localStorage.removeItem('auth_token');
          if (typeof window !== 'undefined') {
            window.location.href = '/';
          }
          return Promise.reject(error);
        }

        // If error is not 401 or request already tried after refresh, reject
        if (error.response?.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        // Skip refresh attempt if no token exists
        const currentToken = localStorage.getItem('auth_token');
        if (!currentToken) {
          return Promise.reject(error);
        }

        if (this.isRefreshing) {
          try {
            const token = await new Promise<string>((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            });
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return this.client(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }
        }

        originalRequest._retry = true;
        this.isRefreshing = true;

        try {
          const response =
            await this.client.post<ApiResponse<AuthResponse>>('/auth/refresh');

          const { token } = response.data;
          if (token) {
            localStorage.setItem('auth_token', token);
          } else {
            throw new Error('No token received from refresh request');
          }

          // Process failed requests queue
          this.failedQueue.forEach(({ resolve }) => resolve(token || ''));
          this.failedQueue = [];

          originalRequest.headers.Authorization = `Bearer ${token}`;
          return this.client(originalRequest);
        } catch (refreshError) {
          // Process failed requests queue
          this.failedQueue.forEach(({ reject }) => reject(refreshError));
          this.failedQueue = [];

          // Clear tokens and redirect
          localStorage.removeItem('auth_token');

          if (typeof window !== 'undefined') {
            window.location.href = '/';
          }

          return Promise.reject(refreshError);
        } finally {
          this.isRefreshing = false;
        }
      }
    );
  }

  // Utility to serialize query params, converting objects to JSON strings
  private serializeQueryParams(
    params?: Record<string, unknown>
  ): URLSearchParams {
    const queryParams = new URLSearchParams();
    if (!params) return queryParams;
    Object.entries(params).forEach(([key, value]) => {
      if (
        value !== null &&
        typeof value === 'object' &&
        !(value instanceof Date) &&
        !(value instanceof File) &&
        !Array.isArray(value)
      ) {
        queryParams.append(key, JSON.stringify(value));
      } else if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    return queryParams;
  }

  async get<T>(
    url: string,
    params?: Record<string, unknown>
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    const queryParams = this.serializeQueryParams(params);
    const queryString = queryParams.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.client.get(fullUrl);
  }

  private async post<T>(
    url: string,
    data?: Record<string, unknown> | FormData,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
    };

    if (data instanceof FormData) {
      delete headers['Content-Type'];
    }

    return this.client.post<T>(url, data, {
      ...config,
      headers,
    });
  }

  async put<T>(
    url: string,
    data?: Record<string, unknown>
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.client.put(url, data);
  }

  async delete<T>(
    url: string,
    data?: Record<string, unknown>
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.client.delete(url, { data });
  }

  // Auth routes
  public async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.client.post<ApiResponse<AuthResponse>>(
      '/auth/login',
      credentials
    );
    const authData = response.data;

    // Only store tokens if MFA is not required
    if (!authData.mfaRequired) {
      if (authData.token) {
        localStorage.setItem('auth_token', authData.token);
      }
    }

    return authData;
  }

  public async register(
    userData: Record<string, unknown>
  ): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>('/auth/register', userData);
    return response.data;
  }

  public async forgotPassword(email: string): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>('/auth/forgot-password', {
        email,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async resetPassword(data: {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
  }): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        '/auth/reset-password',
        data
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      localStorage.removeItem('auth_token');
    }
  }

  public async refreshToken(): Promise<AuthResponse> {
    try {
      const response = await this.post<AuthResponse>('/auth/refresh');
      const { token } = response.data;
      if (token) {
        localStorage.setItem('auth_token', token);
      }

      return response.data;
    } catch (error) {
      // If we get a 401 during refresh, clear tokens and redirect
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('auth_token');
        await this.logout();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
      throw this.handleError(error);
    }
  }

  // Two-Factor Authentication routes
  public async enableTwoFactor(): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>('/auth/2fa/enable');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async confirmTwoFactor(code: string): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>('/auth/2fa/confirm', {
        code,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async verifyMFA(token: string, code: string): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>('/auth/mfa/verify', {
      token,
      code,
    });
    return response.data;
  }

  public async disableTwoFactor(): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>('/auth/2fa/disable');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // User routes
  public async getCurrentUser(): Promise<MeResponse> {
    try {
      const response = await this.get<MeResponse>('/me');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Profile routes
  public async getProfile(): Promise<{
    success: boolean;
    user: UserExtended;
  }> {
    try {
      const response = await this.get<{
        success: boolean;
        user: UserExtended;
      }>('/profile');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateProfile(
    profileData: Record<string, unknown>
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>('/profile', profileData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updatePassword(passwordData: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        '/profile/password',
        passwordData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getUserProfile(
    userId: number | string
  ): Promise<{ success: boolean; user: UserExtended }> {
    try {
      const response = await this.get<{ success: boolean; user: UserExtended }>(
        `/users/${userId}/profile`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getUserTechnicians(userId: number | string): Promise<User[]> {
    try {
      const response = await this.get<ApiResponse<User[]>>(
        `/users/${userId}/technicians`
      );
      return response.data?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getUserClinicians(userId: number | string): Promise<User[]> {
    try {
      const response = await this.get<ApiResponse<User[]>>(
        `/users/${userId}/clinicians`
      );
      return response.data?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getUserPayments(
    userId: number | string,
    params?: Record<string, unknown>
  ): Promise<ApiPaginatedResponse<Payment[]>> {
    try {
      const response = await this.get<ApiPaginatedResponse<Payment[]>>(
        `/users/${userId}/payments`,
        params
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getClinicPayments(
    clinicId: number | string,
    params?: Record<string, unknown>
  ): Promise<ApiPaginatedResponse<Payment[]>> {
    try {
      const response = await this.get<ApiPaginatedResponse<Payment[]>>(
        `/clinics/${clinicId}/payments`,
        params
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateUserProfile(
    userId: number | string,
    profileData: Record<string, unknown>
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/users/${userId}/profile`,
        profileData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async activatePin(values: {
    pin: number;
    pin_confirmation: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        '/profile/pin/activate',
        values
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updatePin(values: {
    current_pin: string;
    pin: string;
    pin_confirmation: string;
  }): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>('/profile/pin', values);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateUserPin(
    userId: number | string,
    pin: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(`/users/${userId}/pin`, {
        pin,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async createUser(
    profileData: Record<string, unknown>,
    role: UserRole
  ): Promise<{ data: { user: User }; success: boolean }> {
    try {
      const response = await this.post<{
        data: { user: User };
        success: boolean;
      }>('/users', {
        ...profileData,
        role,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateUser(
    userId: number | string,
    profileData: Record<string, unknown>
  ): Promise<{ data: { user: User }; success: boolean }> {
    try {
      const response = await this.post<{
        data: { user: User };
        success: boolean;
      }>(`/users/${userId}`, profileData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async approveUser(
    userId: number | string,
    role: UserRole,
    pin: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(`/users/${userId}/verify`, {
        role,
        pin,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateUserPassword(
    userId: number | string,
    passwordData: Record<string, unknown>
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/users/${userId}/password`,
        passwordData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async uploadProfilePicture(
    data: FormData | Record<string, unknown>
  ): Promise<ApiResponse<{ profile_picture_url: string }>> {
    try {
      // If data is not FormData, convert it to FormData
      const formData = data instanceof FormData ? data : new FormData();
      if (!(data instanceof FormData)) {
        Object.entries(data).forEach(([key, value]) => {
          if (value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, String(value));
          }
        });
      }

      const response = await this.client.post<
        ApiResponse<{ profile_picture_url: string }>
      >('/profile/picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...this.getAuthHeaders(),
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Clinic Invitation routes
  public async getClinicInvitations(): Promise<ApiResponse> {
    try {
      const response = await this.get<ApiResponse>('/clinic-invitations');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async createClinicInvitation(
    invitationData: Record<string, unknown>
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        '/clinic-invitations',
        invitationData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async resendClinicInvitation(
    invitationId: number | string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/clinic-invitations/${invitationId}/resend`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async acceptClinicInvitation(token: string): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(`/invitations/accept`, {
        token,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async activateUser(
    params: Record<string, unknown>
  ): Promise<AuthResponse> {
    try {
      const response = await this.post<AuthResponse>(
        `/invitations/users/activate`,
        params
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async activateClinicInvitation(
    params: Record<string, unknown>
  ): Promise<AuthResponse> {
    try {
      const response = await this.post<AuthResponse>(
        `/invitations/activate`,
        params
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async finishClinicInvitation(
    params: Record<string, unknown>
  ): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(
      `/invitations/finish`,
      params
    );
    return response.data;
  }

  public async getUserReferrals(
    userId: string
  ): Promise<ApiPaginatedResponse<UserReferral>> {
    try {
      const response = await this.get<ApiPaginatedResponse<UserReferral>>(
        `/users/${userId}/referrals`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getReferredUsers(
    userId: string
  ): Promise<ApiPaginatedResponse<UserReferral>> {
    try {
      const response = await this.get<ApiPaginatedResponse<UserReferral>>(
        `/users/${userId}/referred-users`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getReferredOrders(
    userId: string,
    role: UserRole,
    status: OrderReferralStatus
  ): Promise<ApiPaginatedResponse<Order>> {
    try {
      const response = await this.get<ApiPaginatedResponse<Order>>(
        `/users/${userId}/referred-orders`,
        {
          role,
          status,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getReferredOrdersCounts(
    userId: string
  ): Promise<ApiResponse<Record<string, Record<string, number>>>> {
    try {
      const response = await this.get<
        ApiResponse<Record<string, Record<string, number>>>
      >(`/users/${userId}/referred-orders/counts`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  public async addUserReferrals(
    userId: string,
    referralId: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/users/${userId}/referrals`,
        {
          referral_id: referralId,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Clinic User Management routes
  public async getClinicUsers(clinicId: number | string): Promise<ApiResponse> {
    try {
      const response = await this.get<ApiResponse>(`/clinic/${clinicId}/users`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateClinicUser(
    clinicId: number | string,
    clinicUserId: number | string,
    userData: Record<string, unknown>
  ): Promise<ApiResponse> {
    try {
      const response = await this.put<ApiResponse>(
        `/clinic/${clinicId}/users/${clinicUserId}`,
        userData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async deleteClinicUser(
    clinicId: number | string,
    clinicUserId: number | string
  ): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>(
        `/clinic/${clinicId}/users/${clinicUserId}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getClinic(id: string): Promise<Clinic> {
    const response = await this.get<ApiResponse<{ data: Clinic }>>(
      `/clinics/${id}`
    );
    return response.data?.data;
  }

  public async getClinics(
    params?: ApiTableParams
  ): Promise<ApiPaginatedResponse<Clinic>> {
    try {
      const response = await this.get<ApiPaginatedResponse<Clinic>>(
        '/clinics',
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async createClinic(clinicData: {
    name: string;
    address?: Address;
  }): Promise<ApiResponse<Clinic>> {
    try {
      const response = await this.post<ApiResponse<Clinic>>(
        '/clinics',
        clinicData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateClinic(
    clinicId: string,
    clinicData: {
      name: string;
      address?: Address;
    }
  ): Promise<ApiResponse<Clinic>> {
    try {
      const response = await this.put<ApiResponse<Clinic>>(
        `/clinics/${clinicId}`,
        clinicData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Technician Assignment routes
  public async assignClinicianTechnician(
    clinicianId: string,
    technicianId: string,
    clinicId: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>('/technician-assignments', {
        clinician_id: clinicianId,
        technician_id: technicianId,
        clinic_id: clinicId,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async unassignClinicianTechnician(id: string): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>(
        `/technician-assignments/${id}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async unassignClinicClinicianTechnician(
    clinicianId: string,
    technicianId: string,
    clinicId?: string | null
  ): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>(
        `/clinics/${clinicId}/clinicians/${clinicianId}/technicians/${technicianId}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getClinicianTechnicians(
    clinicId: number | string,
    clinicianId: number | string
  ): Promise<ApiResponse> {
    try {
      const response = await this.get<ApiResponse>(
        `/clinics/${clinicId}/clinicians/${clinicianId}/technicians`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getTechnicianClinicians(
    clinicId: number | string,
    technicianId: number | string
  ): Promise<ApiResponse> {
    try {
      const response = await this.get<ApiResponse>(
        `/clinics/${clinicId}/technicians/${technicianId}/clinicians`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getUsers(
    params?: ApiUsersParams
  ): Promise<ApiPaginatedResponse<ClinicUser>> {
    try {
      const response = await this.get<ApiPaginatedResponse<ClinicUser>>(
        '/users',
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getPatients(params?: ApiPatientsParams): Promise<{
    data: Patient[];
    pagination: PaginationResponse;
  }> {
    try {
      const response = await this.get<{
        data: Patient[];
        pagination: PaginationResponse;
      }>('/patients', params as Record<string, unknown>);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Generic method that accepts any custom parameters with ApiTableParams
  public async getGenericData<T extends Record<string, unknown>>(
    endpoint: string,
    params?: ApiTableParams<T>
  ): Promise<ApiPaginatedResponse<unknown>> {
    try {
      const response = await this.get<ApiPaginatedResponse<unknown>>(
        endpoint,
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getPatient(patientId: string): Promise<Patient> {
    try {
      const response = await this.get<ApiResponse<{ data: Patient }>>(
        `/patients/${patientId}`
      );
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getPatientLatestHealthHistory(
    patientId: number | string
  ): Promise<ApiResponse> {
    try {
      const response = await this.get<ApiResponse>(
        `/patients/${patientId}/health-history/latest`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getPatientHealthHistory(
    patientId: number | string,
    params?: ApiTableParams
  ): Promise<ApiPaginatedResponse<HealthHistoryGrouped>> {
    try {
      const response = await this.get<
        ApiPaginatedResponse<HealthHistoryGrouped>
      >(
        `/patients/${patientId}/health-history`,
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async createPatientHealthHistory(
    patientId: number | string,
    healthHistory: HealthHistory
  ): Promise<ApiResponse<HealthHistory>> {
    try {
      const response = await this.post<ApiResponse<HealthHistory>>(
        `/patients/${patientId}/health-history`,
        {
          health_history: healthHistory,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async invitePatient(
    patientId: number | string,
    email?: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/patients/${patientId}/invite`,
        { email }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  // Active Clinic routes
  public async getActiveClinic(): Promise<ApiResponse> {
    try {
      const response = await this.get<ApiResponse>('/active-clinic');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async setActiveClinic(
    clinicData: Record<string, unknown>
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        '/active-clinic',
        clinicData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async clearActiveClinic(): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>('/active-clinic');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async verifyClinicConnection(
    clinicId: number | string
  ): Promise<ApiResponse> {
    try {
      const response = await this.get<ApiResponse>(
        `/clinics/${clinicId}/verify-connection`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async verifyUserClinicConnection(
    clinicId: number | string,
    userId: number | string
  ): Promise<ApiResponse> {
    try {
      const response = await this.get<ApiResponse>(
        `/clinics/${clinicId}/users/${userId}/verify-connection`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async switchRole(role: UserRole): Promise<AuthResponse> {
    try {
      const response = await this.post<AuthResponse>('/auth/switch-role', {
        role,
      });
      const authData = response.data;
      if (authData.token) {
        localStorage.setItem('auth_token', authData.token);
      }
      return authData;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async resendMFACode(token: string): Promise<void> {
    await this.post('/auth/mfa/resend', { token });
  }

  public async verifyPassword(
    email: string,
    password: string
  ): Promise<ApiResponse> {
    const response = await this.post<ApiResponse>('/auth/verify-password', {
      email,
      password,
    });
    return response.data;
  }

  public async uploadDocuments(
    documents: Array<{
      file: File;
      type: string;
      issued_at?: string;
      expired_at?: string;
    }>
  ): Promise<{
    success: boolean;
    message: string;
    uploaded_documents: UploadedDocument[];
    documents: UploadedDocument[];
  }> {
    try {
      const formData = new FormData();
      documents.forEach((doc, index) => {
        formData.append(`documents[${index}][file]`, doc.file);
        formData.append(`documents[${index}][type]`, doc.type);
        if (doc.issued_at) {
          formData.append(`documents[${index}][issued_at]`, doc.issued_at);
        }
        if (doc.expired_at) {
          formData.append(`documents[${index}][expired_at]`, doc.expired_at);
        }
      });

      const response = await this.client.post<{
        success: boolean;
        message: string;
        uploaded_documents: UploadedDocument[];
        documents: UploadedDocument[];
      }>('/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async deleteDocument(documentId: string): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>(
        `/documents/${documentId}`
      );
      return response.data;
    } catch (error) {
      console.error('Delete document error:', error);
      throw this.handleError(error);
    }
  }

  public async updateDocument(
    documentId: string,
    data: { issued_at?: string; expired_at?: string }
  ): Promise<ApiResponse> {
    try {
      const response = await this.put<ApiResponse>(
        `/documents/${documentId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Update document error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Backend route: GET /debug-test
   */
  public async debugTest(): Promise<ApiResponse> {
    return (await this.get<ApiResponse>('/debug-test')).data;
  }

  /**
   * Backend route: GET /documents
   */
  public async getDocuments(): Promise<ApiResponse> {
    return (await this.get<ApiResponse>('/documents')).data;
  }

  /**
   * Backend route: POST /documents/{documentType}
   */
  public async uploadDocument(
    documentType: string,
    data: FormData
  ): Promise<ApiResponse> {
    return (await this.post<ApiResponse>(`/documents/${documentType}`, data))
      .data;
  }

  /**
   * Backend route: POST /documents
   */
  public async uploadMultipleDocuments(data: FormData): Promise<ApiResponse> {
    return (await this.post<ApiResponse>('/documents', data)).data;
  }

  public async sendEmail(
    email: string,
    name: string,
    message: string
  ): Promise<ApiResponse> {
    return (
      await this.post<ApiResponse>('/send-email', { email, name, message })
    ).data;
  }

  /**
   * Backend route: DELETE /documents/{document}
   */
  public async deleteDocumentById(documentId: string): Promise<ApiResponse> {
    return (await this.delete<ApiResponse>(`/documents/${documentId}`)).data;
  }

  /**
   * Backend route: POST /users/{user}/verify
   */
  public async verifyUser(userId: number | string): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(`/users/${userId}/verify`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      // If we get a 401 error, clear tokens and redirect
      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token');
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
      }
    }
    return error instanceof Error
      ? error
      : new Error('An unknown error occurred');
  }

  public isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  public async sendOrderReferrals(
    orderId: string,
    userId: string[]
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/orders/${orderId}/referrals`,
        { user_ids: userId }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateOrderReferral(
    orderId: number | string,
    referralId: number | string,
    status: OrderReferralStatus,
    notes?: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.put<ApiResponse>(
        `/orders/${orderId}/referrals/${referralId}`,
        { status, notes }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderReferrals(
    orderId: string
  ): Promise<ApiPaginatedResponse<OrderReferral>> {
    try {
      const response = await this.get<ApiPaginatedResponse<OrderReferral>>(
        `/orders/${orderId}/referrals`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderPossibleReferrals(
    orderId: string
  ): Promise<ApiPaginatedResponse<UserReferral>> {
    try {
      const response = await this.get<ApiPaginatedResponse<UserReferral>>(
        `/orders/${orderId}/possible-referrals`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async deleteOrderReferral(
    orderId: string,
    referralId: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>(
        `/orders/${orderId}/referrals/${referralId}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Send contact form data to backend
   */
  public async sendContactForm(data: unknown): Promise<ApiResponse> {
    try {
      const response = await this.client.post<ApiResponse>('/contact', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async applyRemoteClinician(
    values: Record<string, unknown>,
    role: UserRole
  ): Promise<ApiResponse> {
    try {
      const response = await this.client.post<ApiResponse>(
        `/apply/${role}`,
        values
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async requestUserChanges(
    userId: string,
    message: string,
    pin: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/users/${userId}/request-changes`,
        { message, pin }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateOrder(
    orderId: number | string,
    orderData: OrderFormValues
  ): Promise<ApiResponse<Order>> {
    try {
      const formData = new FormData();
      const { attachments = [], ...restData } = orderData;

      // 1. Handle file attachments
      getAttachmentsToUpload(attachments).forEach(
        (file: File, index: number) => {
          if (file instanceof File) {
            if (!file?.size) throw new Error(`Invalid file at index ${index}`);
            formData.append(`attachments[${index}]`, file);
          } else {
            return true;
          }
        }
      );

      flattenToFormData(formData, restData);

      const response = await this.client.post<ApiResponse<Order>>(
        `/orders/${orderId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...this.getAuthHeaders(),
          },
          transformRequest: (data) => data,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateOrderStatus(
    orderId: string,
    status: OrderStatus
  ): Promise<ApiResponse> {
    const response = await this.post<ApiResponse>(`/orders/${orderId}/status`, {
      status,
    });
    return response.data;
  }

  public async deleteOrder(orderId: string): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async createOrder(
    orderData: OrderFormValues
  ): Promise<ApiResponse<Order>> {
    try {
      const formData = new FormData();
      const { attachments = [], ...restData } = orderData;

      // 1. Handle file attachments
      getAttachmentsToUpload(attachments).forEach(
        (file: File, index: number) => {
          if (!file?.size) throw new Error(`Invalid file at index ${index}`);
          formData.append(`attachments[${index}]`, file);
        }
      );

      flattenToFormData(formData, restData);

      const response = await this.client.post<ApiResponse<Order>>(
        '/orders',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...this.getAuthHeaders(),
          },
          transformRequest: (data) => data, // Prevent axios from transforming formData
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrder(orderId: number | string): Promise<Order> {
    try {
      const response = await this.get<ApiResponse<{ data: Order }>>(
        `/orders/${orderId}`
      );
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderReport(orderId: number | string): Promise<OrderReport> {
    try {
      const response = await this.get<ApiResponse<{ data: OrderReport }>>(
        `/orders/${orderId}/report`
      );
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async createOrderReport(
    orderId: number | string
  ): Promise<ApiResponse<OrderReport>> {
    try {
      const response = await this.post<ApiResponse<OrderReport>>(
        `/orders/${orderId}/report`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrders(
    params?: ApiOrdersParams
  ): Promise<ApiPaginatedResponse<Order>> {
    try {
      const response = await this.get<ApiPaginatedResponse<Order>>(
        '/orders',
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async deleteOrderReport(
    orderId: number | string
  ): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>(
        `/orders/${orderId}/report`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderReports(
    params?: ApiOrderReportsParams
  ): Promise<ApiPaginatedResponse<Order>> {
    try {
      const response = await this.get<ApiPaginatedResponse<Order>>(
        '/order-reports',
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderBillings(
    params?: ApiOrderReportsParams
  ): Promise<ApiPaginatedResponse<OrderBilling>> {
    try {
      const response = await this.get<ApiPaginatedResponse<OrderBilling>>(
        '/order-billings',
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderPayments(
    params?: ApiOrderReportsParams
  ): Promise<ApiPaginatedResponse<OrderPayment>> {
    try {
      const response = await this.get<ApiPaginatedResponse<OrderPayment>>(
        '/order-payments',
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderReportsNotes(
    params?: ApiOrderReportsNotesParams
  ): Promise<ApiPaginatedResponse<Order>> {
    try {
      const response = await this.get<ApiPaginatedResponse<Order>>(
        '/order-reports/notes',
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderConsults(
    params?: ApiOrderReportsNotesParams
  ): Promise<ApiPaginatedResponse<OrderConsult>> {
    try {
      const response = await this.get<ApiPaginatedResponse<OrderConsult>>(
        '/orders/consults',
        params as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderReportsNotesCounts(): Promise<
    ApiResponse<{
      patient: number;
      professional: number;
    }>
  > {
    try {
      const response = await this.get<
        ApiResponse<{
          patient: number;
          professional: number;
        }>
      >(`/order-reports/notes/status-counts`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async saveOrderReportProfessionalNote(
    reportId: number | string,
    data: Record<string, unknown>,
    isDraft: boolean = false,
    pin?: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/order-reports/${reportId}/professional-note`,
        { ...data, is_draft: isDraft, pin }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async saveOrderReportPatientNote(
    reportId: number | string,
    data: Record<string, unknown>,
    isDraft: boolean = false,
    pin?: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/order-reports/${reportId}/patient-note`,
        { ...data, is_draft: isDraft, pin }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getIcd10CodesByCpt(): Promise<ApiResponse<CptToIcd10Mapping>> {
    try {
      const response =
        await this.get<ApiResponse<CptToIcd10Mapping>>(`/get-icd-by-cpt`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async signOrders(
    orderIds: string[],
    pin: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(`/orders/sign`, {
        order_ids: orderIds,
        pin,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async signOrderReports(
    orderIds: string[],
    pin: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(`/order-reports/sign`, {
        order_ids: orderIds,
        pin,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  public async signOrderReportNotes(
    orderIds: string[],
    pin: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/order-reports/notes/sign`,
        {
          order_ids: orderIds,
          pin,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async uploadOrderImaging(
    orderId: string,
    data: OrderImagingFormInterface
  ): Promise<ApiResponse<Order>> {
    const formData = data instanceof FormData ? data : new FormData();

    if (!(data instanceof FormData)) {
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((file: File, index: number) => {
            if (file !== undefined) {
              formData.append(`${key}[${index}]`, file);
            }
          });
        } else {
          formData.append(key, value);
        }
      });
    }

    const response = await this.client.post<ApiResponse<Order>>(
      `/orders/${orderId}/imaging`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...this.getAuthHeaders(),
        },
        transformRequest: (data) => data, // Prevent axios transformation
      }
    );
    return response.data;
  }

  public async updateOrderImagingPrimary(
    orderId: number | string,
    ids: string[]
  ): Promise<ApiResponse<Imaging[]>> {
    try {
      const response = await this.put<ApiResponse<Imaging[]>>(
        '/orders/' + orderId + '/imaging/primary',
        {
          ids,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateOrderImagingReview(
    orderId: number | string,
    data: OrderImagingReportCore,
    isDraft: boolean = false,
    pin: string | null = null
  ): Promise<ApiResponse> {
    try {
      const response = await this.put<ApiResponse>(
        '/orders/' + orderId + '/report',
        {
          ...data,
          is_draft: isDraft,
          pin,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderStatusCounts(): Promise<
    ApiResponse<{
      signed: number;
      pending: number;
      completed: number;
    }>
  > {
    try {
      const response = await this.get<
        ApiResponse<{
          signed: number;
          pending: number;
          completed: number;
        }>
      >(`/orders/status-counts`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderReportsStatusCounts(): Promise<
    ApiResponse<{
      new: number;
      pending: number;
      completed: number;
    }>
  > {
    const response = await this.get<{
      new: number;
      pending: number;
      completed: number;
    }>(`/order-reports/status-counts`);

    return response.data;
  }

  public async getOrderCounts(): Promise<
    ApiResponse<{
      signed: number;
      pending: number;
      completed: number;
    }>
  > {
    try {
      const response = await this.get<{
        signed: number;
        pending: number;
        completed: number;
      }>('/orders/counts');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getOrderReportsCounts(): Promise<
    ApiResponse<{
      new: number;
      pending: number;
      completed: number;
    }>
  > {
    try {
      const response = await this.get<{
        new: number;
        pending: number;
        completed: number;
      }>('/order-reports/counts');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async updateProfileRole(roleId: number | string): Promise<MeResponse> {
    try {
      const response = await this.put<MeResponse>('/profile/role', {
        user_clinic_id: roleId,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async createOrderConsultAddendum(
    orderId: number | string,
    consultId: number | string,
    addendumData: {
      addendum: string;
      addendum_at: dayjs.Dayjs;
    },
    pin: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/orders/${orderId}/consults/${consultId}/addendums`,
        {
          ...addendumData,
          pin,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async createOrderAddendum(
    orderId: number | string,
    addendumData: {
      addendum: string;
      addendum_at: dayjs.Dayjs;
    },
    pin: string
  ): Promise<ApiResponse> {
    try {
      const response = await this.post<ApiResponse>(
        `/orders/${orderId}/report/addendum`,
        {
          ...addendumData,
          pin,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get file URL by type and ID
   * Backend route: GET /get-file/{type}/{id}
   */
  public async getFileUrl(
    type: string,
    id: string
  ): Promise<ApiResponse<{ download_url: string }>> {
    try {
      const response = await this.get<ApiResponse<{ download_url: string }>>(
        `/get-file/${type}/${id}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getRequiredDocuments(role: string): Promise<RequiredDocument[]> {
    try {
      const response = await this.get<RequiredDocument[]>(
        `/documents/required/${role}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getUserCounts(params?: {
    clinic_id?: string;
  }): Promise<ApiResponse<Record<string, Record<string, number>>>> {
    const response = await this.get<Record<string, Record<string, number>>>(
      `/users/counts`,
      params
    );
    return response.data;
  }

  public async updateOrderBilling(
    orderId: string,
    billingData: OrderBillingFormValues,
    cptCode?: string,
    billingId?: string
  ): Promise<ApiResponse> {
    try {
      const formData = new FormData();
      const { attachments = [], ...restData } = billingData;

      // 1. Handle file attachments
      getAttachmentsToUpload(attachments).forEach(
        (file: File, index: number) => {
          if (file instanceof File) {
            if (!file?.size) throw new Error(`Invalid file at index ${index}`);
            formData.append(`attachments[${index}]`, file);
          } else {
            return true;
          }
        }
      );

      flattenToFormData(formData, { ...restData, cpt_code: cptCode });

      if (billingId) {
        const response = await this.client.post<ApiResponse>(
          `/orders/${orderId}/billing/${billingId}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...this.getAuthHeaders(),
            },
            transformRequest: (data) => data,
          }
        );
        return response.data;
      } else {
        const response = await this.client.post<ApiResponse>(
          `/orders/${orderId}/billing`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...this.getAuthHeaders(),
            },
            transformRequest: (data) => data,
          }
        );
        return response.data;
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Payment methods
  public async createOrderPayment(
    orderId: string,
    paymentData: PaymentFormValues
  ): Promise<ApiResponse<OrderPayment>> {
    try {
      const response = await this.post<ApiResponse<OrderPayment>>(
        `/orders/${orderId}/payments`,
        paymentData as unknown as Record<string, unknown>
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Consult methods
  public async saveOrderConsult(
    orderId: string,
    consultData: {
      consult_at?: string | null;
      attachments?: AttachmentWithOriginFileObj[];
      deleted_attachments?: (number | string)[];
      pin?: string;
    }
  ): Promise<ApiResponse> {
    try {
      const formData = new FormData();
      const { attachments = [], ...restData } = consultData;

      getAttachmentsToUpload(attachments).forEach(
        (file: File, index: number) => {
          if (file instanceof File) {
            if (!file?.size) throw new Error(`Invalid file at index ${index}`);
            formData.append(`attachments[${index}]`, file);
          } else {
            return true;
          }
        }
      );

      flattenToFormData(formData, restData);

      const response = await this.client.post<ApiResponse<Order>>(
        `/orders/${orderId}/consult`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...this.getAuthHeaders(),
          },
          transformRequest: (data) => data,
        }
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async getInventory(): Promise<ApiResponse<Inventory[]>> {
    const response = await this.get<ApiResponse<Inventory[]>>(`/inventory`);
    return response.data;
  }
  public async createInventory(
    inventoryData: InventoryFormValues
  ): Promise<ApiResponse<Inventory>> {
    const response = await this.post<ApiResponse<Inventory>>(
      `/inventory`,
      inventoryData as unknown as Record<string, unknown>
    );
    return response.data;
  }
  public async updateInventory(
    inventoryId: string,
    inventoryData: InventoryFormValues
  ): Promise<ApiResponse<Inventory>> {
    const response = await this.put<ApiResponse<Inventory>>(
      `/inventory/${inventoryId}`,
      inventoryData as unknown as Record<string, unknown>
    );
    return response.data;
  }
  public async deleteInventory(
    inventoryId: string
  ): Promise<ApiResponse<Inventory>> {
    const response = await this.delete<ApiResponse<Inventory>>(
      `/inventory/${inventoryId}`
    );
    return response.data;
  }
}

// 2. Flatten and append all other data
const flattenToFormData = (formData: FormData, data: unknown, prefix = '') => {
  if (data === null || data === undefined) {
    formData.append(prefix, '');
  } else if (data === false) {
    formData.append(prefix, '0');
  } else if (data === true) {
    formData.append(prefix, '1');
  } else if (dayjs.isDayjs(data)) {
    formData.append(prefix, data.toISOString());
  } else if (data instanceof File) {
    formData.append(prefix, data, data.name);
  } else if (Array.isArray(data)) {
    data.forEach((item, i) =>
      flattenToFormData(formData, item, `${prefix}[${i}]`)
    );
  } else if (typeof data === 'object') {
    Object.entries(data || {}).forEach(([key, val]) =>
      flattenToFormData(formData, val, prefix ? `${prefix}[${key}]` : key)
    );
  } else if (data !== undefined) {
    formData.append(prefix, String(data));
  }
  // Skip undefined values entirely
};

export const apiClient = new ApiClient();
